import { extractTranslations } from '@assets/i18n/config';
import type { UserNotificationPreference } from '@interfaces/organizationUser';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { useThemeTokens } from '@providers/themeTokenProvider';
import { UserContext } from '@providers/userContextProvider';
import { omit } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationPreferencesSettings } from './notificationPreferencesSettings';

export const NotificationPreferencesModal = ({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation('components');
  const { sys } = useThemeTokens();
  const { userContext, refetch } = useContext(UserContext);
  const [notificationPreferences, setNotificationPreferences] = useState<
    UserNotificationPreference[]
  >([]);

  useEffect(
    () =>
      setNotificationPreferences([
        ...(userContext.notificationPreferences ?? [])
          .filter((e) =>
            (e.notificationGroup?.notificationChannels ?? []).some(
              (el) => el.value,
            ),
          )
          .map((elem) => ({
            notificationGroup: {
              id: elem.notificationGroup.id,
              name: extractTranslations(elem.notificationGroup.name),
              description: elem.notificationGroup.description
                ? extractTranslations(elem.notificationGroup.description)
                : {},
              notificationChannels: (
                elem.notificationGroup?.notificationChannels ?? []
              ).map((nc) => omit(nc, ['__typename'])),
            },
            preferences: omit(elem.preferences, ['__typename']),
          })),
      ] as UserNotificationPreference[]),
    [userContext.notificationPreferences],
  );

  const handleClose = () => {
    refetch();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      sx={{
        '.MuiDialog-paper': {
          minHeight: 400,
          borderRadius: sys.borderRadius.xxl,
        },
      }}
      fullWidth
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          paddingY: 2.5,
          borderBottom: `1px ${sys.color.primaryContainer} solid`,
        }}
      >
        <Typography variant='headingLarge'>
          {t('notificationSettings.title')}
        </Typography>
        <IconButton size='small' onClick={() => setOpen(false)}>
          <CloseIcon sx={{ color: sys.color.onSurface }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ paddingX: 2.5 }}>
        <Box display='flex' flexDirection='column' gap={3} mb={1} mt={0.5}>
          {notificationPreferences.map((elem) => (
            <NotificationPreferencesSettings
              key={elem.notificationGroup.id}
              notificationPreference={elem}
              notificationPreferences={notificationPreferences}
              setNotificationPreferences={setNotificationPreferences}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
